import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { anciennete, codeFamille, eur } from '@troc/shared'
import { api } from '../api'

/**
 * Les statistiques de fond du gérant.
 *
 * Le tableau de bord dit comment va la journée. Cet écran dit ce qu'il faudrait
 * accepter au prochain dépôt : quelles familles partent, lesquelles encombrent,
 * quels clients rapportent — et lesquels apportent beaucoup pour peu de ventes.
 * Les taux d'écoulement comptent donc autant que les euros.
 */

interface DeposantStat {
  id: number
  ref: string
  nom: string
  caCentimes: number
  commissionCentimes: number
  ventes: number
  deposes: number
  ecoulement: number
}

interface FamilleStat {
  categorie: string
  caCentimes: number
  deposes: number
  vendus: number
  ecoulement: number
  delaiMoyenJours: number | null
}

interface Stats {
  global: {
    deposes: number
    vendus: number
    ecoulement: number
    delaiMoyenJours: number | null
  }
  deposants: DeposantStat[]
  familles: FamilleStat[]
  meilleuresVentes: {
    ref: string
    libelle: string
    categorie: string
    totalCentimes: number
    date: string
    deposantNom: string | null
  }[]
  dormeurs: {
    id: number
    ref: string
    titre: string
    categorie: string
    prixCentimes: number
    deposeLe: string
    deposantNom: string | null
  }[]
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' })

/** Une jauge horizontale. Le meilleur de la liste passe en jaune. */
function Jauge({ part, premier }: { part: number; premier: boolean }) {
  return (
    <div className="jauge">
      <span
        className={premier ? 'jauge-remplissage vedette' : 'jauge-remplissage'}
        style={{ width: `${Math.max(2, Math.round(part * 100))}%` }}
      />
    </div>
  )
}

export function Statistiques({ entetes }: { entetes: Record<string, string> }) {
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    api<Stats>('/statistiques', { entetes })
      .then(setStats)
      .catch(() => undefined)
  }, [entetes])

  if (!stats) return <div className="vide">Calcul en cours…</div>

  const maxCaFamille = Math.max(1, ...stats.familles.map((f) => f.caCentimes))
  const maxCaDeposant = Math.max(1, ...stats.deposants.map((d) => d.caCentimes))

  return (
    <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div className="bandeau-kpi">
        <div className="kpi kpi-vedette">
          <div className="etiquette">Taux d’écoulement</div>
          <div className="valeur">{Math.round(stats.global.ecoulement * 100)} %</div>
          <div className="detail">
            {stats.global.vendus} vendus sur {stats.global.deposes} déposés
          </div>
        </div>
        <div className="kpi">
          <div className="etiquette">Délai moyen de vente</div>
          <div className="valeur">
            {stats.global.delaiMoyenJours === null ? '—' : `${stats.global.delaiMoyenJours} j`}
          </div>
          <div className="detail">du dépôt à la vente</div>
        </div>
        <div className="kpi">
          <div className="etiquette">Familles suivies</div>
          <div className="valeur">{stats.familles.length}</div>
          <div className="detail">{stats.deposants.length} clients au classement</div>
        </div>
      </div>

      <section className="bloc-fiche">
        <h3 className="bloc-fiche-titre">Meilleurs déposants</h3>
        <table className="tableau">
          <thead>
            <tr>
              <th>Client</th>
              <th>Chiffre d’affaires</th>
              <th className="a-droite">Commission boutique</th>
              <th className="a-droite">Ventes</th>
              <th className="a-droite">Écoulement</th>
            </tr>
          </thead>
          <tbody>
            {stats.deposants.map((d, rang) => (
              <tr key={d.id}>
                <td className="titre">
                  <Link to={`/deposants/${d.id}`} style={{ color: 'inherit' }}>
                    {d.nom}
                  </Link>
                  <span className="sourdine" style={{ fontSize: 12 }}>
                    {' '}
                    {d.ref}
                  </span>
                </td>
                <td>
                  <div className="ligne-jauge">
                    <span className="chiffre">{eur(d.caCentimes)}</span>
                    <Jauge part={d.caCentimes / maxCaDeposant} premier={rang === 0} />
                  </div>
                </td>
                <td className="a-droite chiffre">{eur(d.commissionCentimes)}</td>
                <td className="a-droite">{d.ventes}</td>
                <td className="a-droite">
                  {Math.round(d.ecoulement * 100)} %
                  <span className="sourdine" style={{ fontSize: 12 }}>
                    {' '}
                    · {d.deposes} déposés
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {stats.deposants.length === 0 && <div className="vide">Aucune vente enregistrée.</div>}
      </section>

      <section className="bloc-fiche">
        <h3 className="bloc-fiche-titre">Ce qui se vend, par famille</h3>
        <table className="tableau">
          <thead>
            <tr>
              <th>Famille</th>
              <th>Chiffre d’affaires</th>
              <th className="a-droite">Vendus / déposés</th>
              <th className="a-droite">Écoulement</th>
              <th className="a-droite">Délai moyen</th>
            </tr>
          </thead>
          <tbody>
            {stats.familles.map((f, rang) => (
              <tr key={f.categorie}>
                <td className="titre">
                  <span className="ref">{codeFamille(f.categorie)}</span> {f.categorie}
                </td>
                <td>
                  <div className="ligne-jauge">
                    <span className="chiffre">{eur(f.caCentimes)}</span>
                    <Jauge part={f.caCentimes / maxCaFamille} premier={rang === 0} />
                  </div>
                </td>
                <td className="a-droite">
                  {f.vendus} / {f.deposes}
                </td>
                <td className="a-droite">{Math.round(f.ecoulement * 100)} %</td>
                <td className="a-droite">
                  {f.delaiMoyenJours === null ? '—' : `${f.delaiMoyenJours} j`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="fiche-colonnes">
        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">Plus belles ventes</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Article</th>
                <th>Client</th>
                <th className="a-droite">Encaissé</th>
                <th className="a-droite">Le</th>
              </tr>
            </thead>
            <tbody>
              {stats.meilleuresVentes.map((v, i) => (
                <tr key={`${v.ref}-${i}`}>
                  <td className="titre">
                    {v.libelle}
                    <span className="sourdine" style={{ fontSize: 12 }}>
                      {' '}
                      {codeFamille(v.categorie)}
                    </span>
                  </td>
                  <td className="sourdine">{v.deposantNom ?? '—'}</td>
                  <td className="a-droite chiffre">{eur(v.totalCentimes)}</td>
                  <td className="a-droite sourdine">
                    {FORMAT_DATE.format(new Date(v.date))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="bloc-fiche">
          <h3 className="bloc-fiche-titre">Ce qui dort en rayon</h3>
          <table className="tableau">
            <thead>
              <tr>
                <th>Article</th>
                <th>Client</th>
                <th className="a-droite">Prix</th>
                <th className="a-droite">Depuis</th>
              </tr>
            </thead>
            <tbody>
              {stats.dormeurs.map((a) => (
                <tr key={a.id}>
                  <td className="titre">
                    <Link to={`/articles/${a.id}`} style={{ color: 'inherit' }}>
                      {a.titre}
                    </Link>
                    <span className="sourdine" style={{ fontSize: 12 }}>
                      {' '}
                      {codeFamille(a.categorie)}
                    </span>
                  </td>
                  <td className="sourdine">{a.deposantNom ?? '—'}</td>
                  <td className="a-droite chiffre">{eur(a.prixCentimes)}</td>
                  <td className="a-droite">{anciennete(a.deposeLe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {stats.dormeurs.length === 0 && <div className="vide">Rien n’attend en rayon.</div>}
        </section>
      </div>
    </div>
  )
}
